'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, Lock, Unlock, Eye, EyeOff, Zap, ZapOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomPasswordGeneratorContent } from '../../data/random-password-generator-content';
import Link from 'next/link';

type CharacterSet = {
    name: string;
    value: string;
    label: string;
};

export default function RandomPasswordGenerator() {
    const [password, setPassword] = useState<string>('');
    const [length, setLength] = useState<number>(16);
    const [copied, setCopied] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [strength, setStrength] = useState<{ score: number; label: string; color: string }>({
        score: 0,
        label: 'Very Weak',
        color: 'bg-red-500'
    });
    const { toast } = useToast();

    const characterSets: CharacterSet[] = [
        { name: 'lowercase', value: 'abcdefghijklmnopqrstuvwxyz', label: 'Lowercase (a-z)' },
        { name: 'uppercase', value: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', label: 'Uppercase (A-Z)' },
        { name: 'numbers', value: '0123456789', label: 'Numbers (0-9)' },
        {
            name: 'symbols',
            value: '!@#$%^&*()_+-=[]{}|;:,.<>?/~`',
            label: 'Symbols (!@#$%^&*)'
        },
        {
            name: 'similar',
            value: 'il1Lo0O',
            label: 'Exclude similar characters (i, l, 1, L, o, 0, O)'
        },
        {
            name: 'ambiguous',
            value: '{}[]()/\'"`~,;:.<>',
            label: 'Exclude ambiguous characters ({}[]()/\'"`~,;:.<>)'
        }
    ];

    const [selectedSets, setSelectedSets] = useState<{ [key: string]: boolean }>({
        lowercase: true,
        uppercase: true,
        numbers: true,
        symbols: true,
        similar: false,
        ambiguous: false
    });

    const toggleCharacterSet = useCallback((setName: string) => {
        setSelectedSets(prev => {
            const newValue = !prev[setName];
            // If this is the last selected character set, don't allow unchecking
            const selectedCount = Object.values(prev).filter(Boolean).length;
            if (selectedCount <= 1 && !newValue) {
                return prev;
            }
            return {
                ...prev,
                [setName]: newValue
            };
        });
    }, []);

    const generatePassword = useCallback((force = false) => {
        // Prevent unnecessary regenerations
        if (password && !force) return;
        // Get all selected character sets
        let chars = '';
        let selectedChars = '';

        characterSets.slice(0, 4).forEach(set => {
            if (selectedSets[set.name]) {
                chars += set.value;
                selectedChars += set.value;
            }
        });

        // Remove excluded characters
        if (selectedSets.similar) {
            const similarChars = characterSets.find(s => s.name === 'similar')?.value || '';
            chars = chars.split('').filter(char => !similarChars.includes(char)).join('');
        }

        if (selectedSets.ambiguous) {
            const ambiguousChars = characterSets.find(s => s.name === 'ambiguous')?.value || '';
            chars = chars.split('').filter(char => !ambiguousChars.includes(char)).join('');
        }

        // Ensure at least one character set is selected
        if (chars.length === 0) {
            toast({
                title: 'Error',
                description: 'Please select at least one character set',
                variant: 'destructive',
            });
            return;
        }

        // Generate password
        let newPassword = '';
        const array = new Uint32Array(length);
        window.crypto.getRandomValues(array);

        // First, ensure at least one character from each selected set
        const selectedSetsList = characterSets.slice(0, 4).filter(set => selectedSets[set.name]);
        for (let i = 0; i < selectedSetsList.length; i++) {
            const set = selectedSetsList[i];
            const randomIndex = Math.floor(Math.random() * set.value.length);
            const char = set.value[randomIndex];
            // Insert at random position
            const pos = Math.floor(Math.random() * (newPassword.length + 1));
            newPassword = newPassword.slice(0, pos) + char + newPassword.slice(pos);
        }

        // Fill the rest of the password
        for (let i = newPassword.length; i < length; i++) {
            const randomIndex = array[i] % chars.length;
            const char = chars[randomIndex];
            // Insert at random position
            const pos = Math.floor(Math.random() * (newPassword.length + 1));
            newPassword = newPassword.slice(0, pos) + char + newPassword.slice(pos);
        }

        // Ensure the password meets the length requirement
        newPassword = newPassword.slice(0, length);

        // Only update if the password actually changed
        if (newPassword !== password) {
            setPassword(newPassword);
            setCopied(false);
            updatePasswordStrength(newPassword);
        }
    }, [length, selectedSets, characterSets, toast]);

    const updatePasswordStrength = useCallback((pwd: string) => {
        if (!pwd) return;
        let score = 0;

        // Length check
        if (pwd.length >= 12) score += 2;
        else if (pwd.length >= 8) score += 1;

        // Character variety
        const hasLower = /[a-z]/.test(pwd);
        const hasUpper = /[A-Z]/.test(pwd);
        const hasNumbers = /[0-9]/.test(pwd);
        const hasSymbols = /[^a-zA-Z0-9]/.test(pwd);

        if (hasLower && hasUpper) score += 1;
        if (hasNumbers) score += 1;
        if (hasSymbols) score += 1;

        // Deductions for patterns
        const hasRepeats = /(.)\1{2,}/.test(pwd);
        const hasSequence = /(abc|bcd|cde|def|efg|fgh|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(pwd);

        if (hasRepeats) score = Math.max(0, score - 1);
        if (hasSequence) score = Math.max(0, score - 1);

        // Determine strength level
        let label, color;
        if (score <= 1) {
            label = 'Very Weak';
            color = 'bg-red-500';
        } else if (score <= 2) {
            label = 'Weak';
            color = 'bg-orange-500';
        } else if (score <= 3) {
            label = 'Moderate';
            color = 'bg-yellow-500';
        } else if (score <= 4) {
            label = 'Strong';
            color = 'bg-green-500';
        } else {
            label = 'Very Strong';
            color = 'bg-emerald-500';
        }

        setStrength({ score, label, color });
    }, []);

    const copyToClipboard = useCallback(() => {
        if (!password) return;

        navigator.clipboard.writeText(password);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Password has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    }, [password, toast]);

    const handleLengthChange = useCallback((value: number[]) => {
        const newLength = value[0];
        if (newLength !== length) {
            setLength(newLength);
            // Only regenerate if the length actually changed
            if (newLength > 0) {
                generatePassword(true);
            }
        }
    }, [length, generatePassword]);

    // Generate initial password on mount
    useEffect(() => {
        if (!password) {
            generatePassword();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const passwordStrengthMeter = useMemo(() => {
        return (
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                <div
                    className={`h-2.5 rounded-full ${strength.color}`}
                    style={{ width: `${(strength.score / 5) * 100}%` }}
                ></div>
            </div>
        );
    }, [strength]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Random Password Generator">
                Generate strong, secure passwords with our free online tool. Create custom passwords with advanced options for maximum security and memorability.
            </ToolPageIntro>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Generate Password</CardTitle>
                            <CardDescription>Create a strong, random password</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            readOnly
                                            className="text-lg font-mono h-12 pr-12"
                                        />
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex space-x-1">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="h-8 w-8"
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={copyToClipboard}
                                                disabled={!password}
                                                className="h-8 w-8"
                                                aria-label="Copy to clipboard"
                                            >
                                                {copied ? (
                                                    <Check className="h-4 w-4" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => generatePassword(true)}
                                        className="h-12 px-4 whitespace-nowrap"
                                    >
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Generate
                                    </Button>
                                </div>

                                <div className="mt-4">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm font-medium">Password Strength: {strength.label}</span>
                                        <span className="text-xs text-muted-foreground">{length} characters</span>
                                    </div>
                                    {passwordStrengthMeter}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <Label>Password Length: {length}</Label>
                                        <span className="text-sm text-muted-foreground">{length >= 12 ? 'Strong' : length >= 8 ? 'Good' : 'Weak'}</span>
                                    </div>
                                    <Slider
                                        min={4}
                                        max={64}
                                        step={1}
                                        value={[length]}
                                        onValueChange={handleLengthChange}
                                        className="py-4"
                                    />
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>4</span>
                                        <span>64</span>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <Label>Character Sets</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {characterSets.map((set) => (
                                            <div key={set.name} className="flex items-center space-x-2">
                                                <Switch
                                                    id={set.name}
                                                    checked={selectedSets[set.name]}
                                                    onCheckedChange={() => toggleCharacterSet(set.name)}
                                                    disabled={!selectedSets[set.name] &&
                                                        Object.values(selectedSets).filter(Boolean).length === 1 &&
                                                        selectedSets[set.name] === false}
                                                />
                                                <Label htmlFor={set.name} className="text-sm">
                                                    {set.label}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Password Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <Zap className="h-4 w-4 mr-2 text-yellow-500" />
                                    Creating Strong Passwords
                                </h3>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li>Use at least 12-16 characters</li>
                                    <li>Include a mix of uppercase and lowercase letters</li>
                                    <li>Add numbers and special characters</li>
                                    <li>Avoid common words and patterns</li>
                                    <li>Don't reuse passwords across different sites</li>
                                </ul>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <Lock className="h-4 w-4 mr-2 text-emerald-500" />
                                    Password Security
                                </h3>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li>Use a password manager to store your passwords securely</li>
                                    <li>Enable two-factor authentication (2FA) when available</li>
                                    <li>Change passwords immediately if a service reports a breach</li>
                                    <li>Never share your passwords via email or messaging apps</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Password Strength</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Very Weak</span>
                                    <div className="h-2 w-2 rounded-full bg-red-500"></div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Weak</span>
                                    <div className="h-2 w-2 rounded-full bg-orange-500"></div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Moderate</span>
                                    <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Strong</span>
                                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Very Strong</span>
                                    <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                                </div>
                            </div>

                            <div className="pt-4 border-t">
                                <h4 className="font-medium mb-2">Password Requirements:</h4>
                                <ul className="space-y-2 text-sm">
                                    <li className="flex items-center">
                                        <Check className="h-4 w-4 mr-2 text-green-500" />
                                        At least {length >= 8 ? '8' : '12-16'} characters
                                    </li>
                                    <li className="flex items-center">
                                        {password.match(/[a-z]/) ? (
                                            <Check className="h-4 w-4 mr-2 text-green-500" />
                                        ) : (
                                            <ZapOff className="h-4 w-4 mr-2 text-red-500" />
                                        )}
                                        Lowercase letters (a-z)
                                    </li>
                                    <li className="flex items-center">
                                        {password.match(/[A-Z]/) ? (
                                            <Check className="h-4 w-4 mr-2 text-green-500" />
                                        ) : (
                                            <ZapOff className="h-4 w-4 mr-2 text-red-500" />
                                        )}
                                        Uppercase letters (A-Z)
                                    </li>
                                    <li className="flex items-center">
                                        {password.match(/[0-9]/) ? (
                                            <Check className="h-4 w-4 mr-2 text-green-500" />
                                        ) : (
                                            <ZapOff className="h-4 w-4 mr-2 text-red-500" />
                                        )}
                                        Numbers (0-9)
                                    </li>
                                    <li className="flex items-center">
                                        {password.match(/[^a-zA-Z0-9]/) ? (
                                            <Check className="h-4 w-4 mr-2 text-green-500" />
                                        ) : (
                                            <ZapOff className="h-4 w-4 mr-2 text-red-500" />
                                        )}
                                        Special characters (!@#$%^&*)
                                    </li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Password Security Check</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <h4 className="font-medium">Common Password Mistakes:</h4>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li>Using personal information (name, birthdate, etc.)</li>
                                    <li>Sequential characters (123456, abcdef)</li>
                                    <li>Repeated characters (111111, aaaaaa)</li>
                                    <li>Common dictionary words (password, qwerty)</li>
                                    <li>Keyboard patterns (qwerty, asdfgh)</li>
                                </ul>
                            </div>

                            <div className="pt-4 border-t">
                                <h4 className="font-medium mb-2">Did You Know?</h4>
                                <p className="text-sm">
                                    A 12-character password with a mix of character types would take centuries to crack,
                                    while an 8-character password with only lowercase letters could be cracked in minutes.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomPasswordGeneratorContent.title}
                description={randomPasswordGeneratorContent.description}
                content={randomPasswordGeneratorContent.content}
                faqs={randomPasswordGeneratorContent.faqs}
                relatedTools={randomPasswordGeneratorContent.relatedTools}
            />
        </div>
    );
}
